import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Users,
  FileText,
  Calendar,
  Mail,
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  Brain,
  CheckCircle,
  AlertCircle,
  Clock,
  ArrowRight
} from 'lucide-react';

// Simple Pie Chart Component
function SimplePieChart({ data, title, colorScheme = 'blue' }) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  if (total === 0) return <div className="text-center text-muted-foreground py-8">No data</div>;
  
  // Secret Sauce brand colors - amber for EDGE, purple for CORE
  const colors = colorScheme === 'blue' 
    ? ['#D97706', '#F59E0B', '#FBBF24', '#FCD34D', '#FDE68A', '#FEF3C7']  // Amber palette for EDGE
    : ['#7C3AED', '#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE', '#EDE9FE'];  // Purple palette for CORE
  
  // Calculate segments with cumulative angles
  const segments = data.reduce((acc, item, index) => {
    const percentage = (item.value / total) * 100;
    const cumulative = acc.length > 0 ? acc[acc.length - 1].cumulative : 0;
    const startAngle = cumulative * 3.6; // 360/100
    acc.push({ 
      ...item, 
      percentage, 
      startAngle, 
      cumulative: cumulative + percentage,
      color: colors[index % colors.length] 
    });
    return acc;
  }, []);
  
  // Generate conic gradient
  let gradient = 'conic-gradient(';
  let currentAngle = 0;
  segments.forEach((seg, i) => {
    const endAngle = currentAngle + (seg.percentage * 3.6);
    gradient += `${seg.color} ${currentAngle}deg ${endAngle}deg`;
    if (i < segments.length - 1) gradient += ', ';
    currentAngle = endAngle;
  });
  gradient += ')';
  
  return (
    <div>
      <h3 className="font-semibold mb-4">{title}</h3>
      <div className="flex items-center gap-6">
        <div 
          className="w-32 h-32 rounded-full" 
          style={{ background: gradient }}
        />
        <div className="flex-1 space-y-1">
          {segments.slice(0, 5).map((seg, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: seg.color }} />
              <span className="truncate flex-1">{seg.name}</span>
              <span className="text-muted-foreground">{seg.value}</span>
            </div>
          ))}
          {segments.length > 5 && (
            <div className="text-xs text-muted-foreground">+{segments.length - 5} more</div>
          )}
        </div>
      </div>
    </div>
  );
}

// Funnel Visualization
function FunnelChart({ stages }) {
  const maxCount = Math.max(...stages.map(s => s.count), 1);
  
  return (
    <div className="space-y-2">
      {stages.map((stage, i) => {
        const width = Math.max((stage.count / maxCount) * 100, 10);
        return (
          <div key={i} className="flex items-center gap-3">
            <div className="w-32 text-sm truncate text-right">{stage.name}</div>
            <div className="flex-1 flex items-center gap-2">
              <div 
                className="h-8 rounded transition-all duration-500 flex items-center justify-end px-2"
                style={{ 
                  width: `${width}%`,
                  backgroundColor: `hsl(200, 64%, ${40 + i * 5}%)` 
                }}
              >
                <span className="text-xs text-white font-medium">{stage.count}</span>
              </div>
              <span className="text-xs text-muted-foreground w-12">{stage.rate}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminDashboardHome() {
  const [loading, setLoading] = useState(true);
  const [funnel, setFunnel] = useState(null);
  const [distributions, setDistributions] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [activities, setActivities] = useState([]);
  const [basicStats, setBasicStats] = useState(null);
  const [timeFilter, setTimeFilter] = useState('30d'); // '7d', '30d', '90d', 'all'
  const [trends, setTrends] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, [timeFilter]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [funnelRes, distRes, dropRes, actRes, statsRes] = await Promise.all([
        axios.get(`/api/admin/analytics/funnel?period=${timeFilter}`),
        axios.get(`/api/admin/analytics/distributions?period=${timeFilter}`),
        axios.get(`/api/admin/analytics/dropoff?period=${timeFilter}`),
        axios.get(`/api/admin/analytics/activity?period=${timeFilter}`),
        axios.get(`/api/admin/analytics?period=${timeFilter}`)
      ]);
      
      setFunnel(funnelRes.data.funnel);
      setDistributions(distRes.data);
      setDropoff(dropRes.data.dropoff);
      setActivities(actRes.data.activities);
      setBasicStats(statsRes.data.analytics);
      
      // Calculate simple trends (mock for now - backend would provide real data)
      setTrends({
        users: Math.floor(Math.random() * 30) - 10,
        assessments: Math.floor(Math.random() * 40) - 15,
        applications: Math.floor(Math.random() * 20) - 5,
        bookings: Math.floor(Math.random() * 25) - 10
      });
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (action) => {
    const icons = {
      'assessment_started': <FileText className="w-4 h-4 text-blue-500" />,
      'assessment_completed': <CheckCircle className="w-4 h-4 text-green-500" />,
      'booking_created': <Calendar className="w-4 h-4 text-purple-500" />,
      'application_submitted': <Target className="w-4 h-4 text-amber-500" />,
      'application_status_updated': <Activity className="w-4 h-4 text-teal-500" />,
      'email_opened': <Mail className="w-4 h-4 text-pink-500" />,
      'admin_note_added': <FileText className="w-4 h-4 text-gray-500" />,
    };
    return icons[action] || <Activity className="w-4 h-4 text-gray-400" />;
  };

  const formatActivityMessage = (activity) => {
    const messages = {
      'assessment_started': 'started an assessment',
      'assessment_completed': 'completed an assessment',
      'booking_created': 'booked a session',
      'application_submitted': 'submitted an application',
      'application_status_updated': 'application status changed',
      'email_opened': 'opened an email',
      'admin_note_added': 'admin note added',
      'manual_email_sent': 'received manual email'
    };
    return messages[activity.action] || activity.action;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    );
  }

  // Trend indicator component
  const TrendIndicator = ({ value }) => {
    if (!value || value === 0) return null;
    const isPositive = value > 0;
    return (
      <span className={`flex items-center gap-0.5 text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
        {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {Math.abs(value)}%
      </span>
    );
  };

  return (
    <div className="space-y-8" data-testid="admin-dashboard-home">
      {/* Time Filter */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Executive Overview</h3>
        <div className="flex items-center gap-2 bg-muted/50 rounded-lg p-1">
          {[
            { value: '7d', label: '7 Days' },
            { value: '30d', label: '30 Days' },
            { value: '90d', label: '90 Days' },
            { value: 'all', label: 'All Time' }
          ].map(period => (
            <button
              key={period.value}
              onClick={() => setTimeFilter(period.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                timeFilter === period.value 
                  ? 'bg-background shadow text-foreground' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics with Trends */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-16 h-16 bg-blue-500/10 rounded-full -mr-4 -mt-4" />
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="w-4 h-4" /> Total Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <p className="text-3xl font-bold">{basicStats?.totalUsers || 0}</p>
              <TrendIndicator value={trends?.users} />
            </div>
          </CardContent>
        </Card>
        
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-16 h-16 bg-amber-500/10 rounded-full -mr-4 -mt-4" />
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FileText className="w-4 h-4" /> Assessments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold">{basicStats?.totalAssessments || 0}</p>
                <p className="text-xs text-muted-foreground">{basicStats?.completedAssessments || 0} completed</p>
              </div>
              <TrendIndicator value={trends?.assessments} />
            </div>
          </CardContent>
        </Card>
        
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-16 h-16 bg-green-500/10 rounded-full -mr-4 -mt-4" />
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Bookings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <p className="text-3xl font-bold">{basicStats?.totalBookings || 0}</p>
              <TrendIndicator value={trends?.bookings} />
            </div>
          </CardContent>
        </Card>
        
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-16 h-16 bg-purple-500/10 rounded-full -mr-4 -mt-4" />
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Target className="w-4 h-4" /> Applications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold">{basicStats?.totalApplications || 0}</p>
                <p className="text-xs text-muted-foreground">{basicStats?.acceptedApplications || 0} accepted</p>
              </div>
              <TrendIndicator value={trends?.applications} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Funnel & Drop-off */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Funnel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" /> Conversion Funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            {funnel && <FunnelChart stages={funnel.stages} />}
            {funnel && (
              <div className="mt-4 p-3 bg-primary/5 rounded-lg">
                <p className="text-sm">
                  <strong>Overall Conversion:</strong> {funnel.overallConversion}% of registered users become accepted participants
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Drop-off Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5" /> Drop-off Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dropoff && (
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">EDGE Diagnostic</span>
                    <Badge variant={dropoff.fear.completionRate >= 70 ? 'default' : 'secondary'}>
                      {dropoff.fear.completionRate}% completion
                    </Badge>
                  </div>
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-ss-orange transition-all" 
                      style={{ width: `${dropoff.fear.completionRate}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>{dropoff.fear.completed} completed</span>
                    <span>{dropoff.fear.abandoned} abandoned</span>
                  </div>
                  {dropoff.fear.avgQuestionsBeforeDropoff > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Avg. {dropoff.fear.avgQuestionsBeforeDropoff} questions before abandonment
                    </p>
                  )}
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">CORE Diagnostic</span>
                    <Badge variant={dropoff.archetype.completionRate >= 70 ? 'default' : 'secondary'}>
                      {dropoff.archetype.completionRate}% completion
                    </Badge>
                  </div>
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-ss-teal transition-all" 
                      style={{ width: `${dropoff.archetype.completionRate}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>{dropoff.archetype.completed} completed</span>
                    <span>{dropoff.archetype.abandoned} abandoned</span>
                  </div>
                  {dropoff.archetype.avgQuestionsBeforeDropoff > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Avg. {dropoff.archetype.avgQuestionsBeforeDropoff} questions before abandonment
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Distributions */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> Edge Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {distributions && (
              <SimplePieChart 
                data={distributions.fearDistribution} 
                title="" 
                colorScheme="blue" 
              />
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5" /> Core Archetype Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {distributions && (
              <SimplePieChart 
                data={distributions.archetypeDistribution} 
                title="" 
                colorScheme="purple" 
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" /> Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activities.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No recent activity</p>
          ) : (
            <div className="space-y-3">
              {activities.slice(0, 10).map((activity, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded hover:bg-muted/50">
                  {getActivityIcon(activity.action)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium">{activity.userName || activity.userEmail || 'User'}</span>
                      {' '}{formatActivityMessage(activity)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {activity.createdAt ? formatDateTime(activity.createdAt) : ''}
                    </p>
                  </div>
                  <button 
                    onClick={() => window.location.href = `/admin/participants/${activity.userId}`}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    View <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
